/*
 * The screen behind an address that leads nowhere (VISUAL.md §7.17), and the door every screen that
 * has to send a reader somewhere else asks for.
 *
 * It is one component and not one per area, because "this is not here, and here is the way back" is
 * one sentence in the product: the router draws it for an unknown path, and a module draws it for an
 * address of its own that no longer resolves — a channel that was archived, renamed or left, which is
 * the broken link a team actually produces, because a channel's short name *is* its address. A module
 * that rendered its own grey paragraph instead had the commonest of the two cases looking like a
 * fault and the rarer one looking designed (N5-R1-3).
 *
 * What a caller may change is the two sentences and one door; the picture, the heading level and the
 * way to Home are the screen's own. The lead stays the default wherever the reason is "the address
 * does not resolve", because "the page may have been moved or archived" is the true half of the
 * explanation in every case.
 */

import { Link } from "@tanstack/react-router"
import { conversationHref } from "../../routes/links.ts"
import { ListState } from "../../ui/index.ts"
import { t } from "../i18n/index.ts"
import { conversationAddress } from "../store/conversationName.ts"
import type { ConversationSlice } from "../store/model.ts"
import { useModel } from "./context.tsx"
import { ROCKET_IN_SPACE } from "./FirstRunCard.tsx"
import "./home.css"
import type { PlaceDoor } from "./SetupChecklist.tsx"

/**
 * The channel the doors offer — the first run's *Open #allgemein*, the 404's way back, the catch-up's
 * "up to date" — named the way the whole product names a place (`core/store/conversationName.ts`).
 * It is a channel and not merely the landing conversation, because "start where everybody is" is
 * the sentence the doors make and a direct conversation is not that place.
 *
 * "Where everybody is" is the most populated public channel (D-10 D2). The instance's default
 * channel (`chat.default_channel`, an administrator's setting) is not readable by a member this
 * round, and does not need to be: when it is set, every account is made a member of it, so it is in
 * this list — and for a new account it is usually the only channel there. A private channel is
 * offered only when there is no public one: a closed channel is by definition not where everybody is,
 * and still better than no door on an instance that has only those.
 *
 * The model is read in stable slices and the answer derived here rather than inside a selector: a
 * selector that built this object would hand `useStore` a new value on every render (REUSE C5,
 * `core/shell/context.tsx`).
 */
export function useFirstChannel(): PlaceDoor | undefined {
	const conversations = useModel((model) => model.conversations)
	const order = useModel((model) => model.conversationOrder)
	const users = useModel((model) => model.users)
	const me = useModel((model) => model.me)
	const kinds = ["channel", "private"] as const
	for (const kind of kinds) {
		let best: ConversationSlice | undefined
		for (const id of order) {
			const slice = conversations[id]
			if (slice === undefined || slice.meta.archivedAt !== undefined) continue
			if (slice.meta.kind !== kind || conversationHref(slice.meta) === null) continue
			const members = slice.meta.memberIds?.length ?? 0
			if (best === undefined || members > (best.meta.memberIds?.length ?? 0)) best = slice
		}
		if (best !== undefined) {
			const href = conversationHref(best.meta)
			if (href !== null) {
				return { href, label: conversationAddress({ users, conversations, me }, best.meta.id) }
			}
		}
	}
	return undefined
}

export function NotFoundState({
	/** The sentence that says what was not found; the page's own by default. */
	title,
	/** Why it may be gone; the page's own by default, which is true of every missing address. */
	lead,
	/**
	 * The way back into the area the address belonged to — AI Chats' *Your AI chats*. It is the
	 * primary door where it is given, because somebody who followed a link to a page that has moved
	 * is looking for that page and not for Home, and the two doors below it then become one: Home
	 * stays, and the channel — which is the way back for an address that names a conversation — is
	 * not what a missing AI chat leads to.
	 */
	door,
}: {
	title?: string
	lead?: string
	door?: { href: string; label: string }
}) {
	const channel = useFirstChannel()
	return (
		<div className="tb-page tb-home tb-home__empty">
			<ListState.Empty
				illustration={<img src={ROCKET_IN_SPACE} alt="" />}
				// The page's one heading, drawn as a real `h1` (`ListState.Empty`'s `heading`): the state's
				// title is a paragraph on every other screen, because the frame's own `h1` stands above
				// it there, and here the sentence *is* the page.
				heading={1}
				title={title ?? t("shell.notFound.title")}
				lead={lead ?? t("shell.notFound.body")}
				action={
					<div className="tb-home__doors">
						{/* Links and not Buttons with an href: the way back has to be an in-place
						    navigation, and they wear the button's classes so that they read as the way
						    out of the page. */}
						{door === undefined ? null : (
							<Link to={door.href} className="tb-button tb-button--primary">
								{door.label}
							</Link>
						)}
						<Link
							to="/"
							className={
								door === undefined
									? "tb-button tb-button--primary"
									: "tb-button tb-button--secondary"
							}
						>
							{t("shell.notFound.home")}
						</Link>
						{door !== undefined || channel === undefined ? null : (
							<Link to={channel.href} className="tb-button tb-button--secondary">
								{t("shell.home.open", { channel: channel.label })}
							</Link>
						)}
					</div>
				}
			/>
		</div>
	)
}
